class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
/* Shezair premium live facet polish */
(function() {
  function addStyles() {
    if (document.getElementById('shz-live-facet-polish-style')) return;
    var style = document.createElement('style');
    style.id = 'shz-live-facet-polish-style';
    style.textContent = `\n      #main-collection-filters { max-height: calc(100vh - 118px) !important; overflow-y: auto !important; overflow-x: hidden !important; padding-right: 14px !important; padding-bottom: 32px !important; scrollbar-width: thin !important; scrollbar-color: #d8d3cf transparent !important; }\n      #main-collection-filters::-webkit-scrollbar { width: 4px !important; }\n      #main-collection-filters::-webkit-scrollbar-thumb { background: #d8d3cf !important; border-radius: 999px !important; }
      #main-collection-filters .active-facets-vertical-filter { margin: 0 0 28px !important; padding: 0 0 22px !important; border-bottom: 1px solid #dedad6 !important; }
      #main-collection-filters .active-facets__button-wrapper { display: none !important; }
      #main-collection-filters .facets__heading--vertical { margin: 0 !important; color: #1a1210 !important; font-family: Cormorant Garamond, Georgia, serif !important; font-size: 34px !important; font-weight: 400 !important; line-height: 1 !important; letter-spacing: 0 !important; text-transform: none !important; }
      #main-collection-filters .facets__disclosure-vertical { margin: 0 0 28px !important; padding: 0 !important; border: 0 !important; }
      #main-collection-filters .facets__summary { min-height: 28px !important; padding: 0 !important; color: #171717 !important; font-family: Inter, Arial, sans-serif !important; font-size: 18px !important; font-weight: 400 !important; line-height: 1.25 !important; letter-spacing: 0 !important; text-transform: none !important; }
      #main-collection-filters .facets__summary > div { align-items: center !important; justify-content: space-between !important; width: 100% !important; }
      #main-collection-filters .facets__summary-label, #main-collection-filters .facets__summary:hover .facets__summary-label { text-decoration: none !important; }
      #main-collection-filters .facets__summary .icon-caret { right: 0 !important; width: 12px !important; height: 12px !important; color: #171717 !important; }
      #main-collection-filters .facets__display-vertical { padding: 24px 0 0 !important; }
      #main-collection-filters .facets__list { display: grid !important; gap: 18px !important; }
      #main-collection-filters .facets-layout-list, #main-collection-filters .list-menu__item, #main-collection-filters .facets__item { margin: 0 !important; padding: 0 !important; }
      #main-collection-filters .facet-checkbox { display: flex !important; align-items: center !important; gap: 12px !important; min-height: 20px !important; margin: 0 !important; padding: 0 !important; color: #292929 !important; font-family: Inter, Arial, sans-serif !important; font-size: 16px !important; font-weight: 400 !important; line-height: 1.35 !important; }
      #main-collection-filters .facet-checkbox input[type='checkbox'] { position: relative !important; z-index: 1 !important; display: inline-block !important; width: 18px !important; height: 18px !important; min-width: 18px !important; min-height: 18px !important; margin: 0 !important; padding: 0 !important; flex: 0 0 18px !important; appearance: none !important; -webkit-appearance: none !important; border: 1px solid #b9b9b9 !important; border-radius: 2px !important; background: #fff !important; box-shadow: none !important; }
      #main-collection-filters .facet-checkbox input[type='checkbox']:checked { border-color: #111 !important; background: #111 !important; }
      #main-collection-filters .facet-checkbox input[type='checkbox']:checked::after { content: '' !important; position: absolute !important; left: 5px !important; top: 2px !important; width: 5px !important; height: 10px !important; border: solid #fff !important; border-width: 0 1.5px 1.5px 0 !important; transform: rotate(45deg) !important; }
      #main-collection-filters .facet-checkbox > svg, #main-collection-filters .facet-checkbox .icon-checkmark { display: none !important; }
      #main-collection-filters [data-shz-hide-default-facet='true'] { display: none !important; }
      #main-collection-filters .shz-manual-facets { display: none !important; }
    `;
    document.head.appendChild(style);
  }
  function polish() {
    var root = document.getElementById('main-collection-filters');
    if (!root) return;
    addStyles();
    var heading = root.querySelector('.facets__heading--vertical');
    if (heading) heading.textContent = 'Filters';
    root.querySelectorAll('.facets__disclosure-vertical').forEach(function(detail) {
      var label = detail.querySelector('.facets__summary-label, .facets__summary span');
      var text = label ? label.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '';
      if (text.indexOf('availability') !== -1 || text.indexOf('price') !== -1) {
        detail.setAttribute('data-shz-hide-default-facet', 'true');
      }
      if (text.indexOf('nail shape') !== -1 || text === 'shape') {
        if (label) label.textContent = 'Shop By Shape';
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', polish);
  else polish();
  document.addEventListener('shopify:section:load', polish);
  document.addEventListener('facet:updated', polish);
  setTimeout(polish, 300);
  setTimeout(polish, 1000);
  setTimeout(polish, 2500);
})();
/* End Shezair premium live facet polish */
/* Shezair hide zero manual facet options */
(function() {
  function hideZeroOptions() {
    document.querySelectorAll('#main-collection-filters .shz-filter-option').forEach(function(label) {
      var text = label.textContent.replace(/\s+/g, ' ').trim();
      var input = label.querySelector('input');
      if (text.indexOf('(0)') !== -1 && !(input && input.checked)) label.style.display = 'none';
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hideZeroOptions);
  else hideZeroOptions();
  setTimeout(hideZeroOptions, 500);
  setTimeout(hideZeroOptions, 1500);
})();
/* End Shezair hide zero manual facet options */
/* Shezair collection proportion tuning */
(function(){function s(){if(document.getElementById('shz-proportion-tuning-style'))return;var e=document.createElement('style');e.id='shz-proportion-tuning-style';e.textContent="@media screen and (min-width: 990px) {\n  .facets-vertical.page-width { align-items: flex-start !important; gap: 42px !important; padding-left: 40px !important; padding-right: 38px !important; }\n  .facets-vertical .facets-wrapper { flex: 0 0 306px !important; width: 306px !important; min-width: 306px !important; max-width: 306px !important; padding: 0 26px 0 0 !important; }\n  #main-collection-filters { max-height: calc(100vh - 112px) !important; padding-right: 18px !important; }\n  #main-collection-filters .facets__heading--vertical { font-size: 36px !important; }\n  #main-collection-filters .facets__summary, #main-collection-filters .shz-filter-toggle { font-size: 19px !important; }\n  #main-collection-filters .facet-checkbox, #main-collection-filters .shz-filter-option { font-size: 16.5px !important; }\n  .facets-vertical .product-grid-container { flex: 1 1 auto !important; min-width: 0 !important; }\n  .facets-vertical-sort.page-width { max-width: none !important; padding-left: 388px !important; padding-right: 38px !important; }\n  #product-grid.grid--4-col-desktop, .product-grid.grid--4-col-desktop { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)) !important; column-gap: 24px !important; row-gap: 72px !important; }\n  #product-grid .grid__item, .product-grid .grid__item { width: 100% !important; max-width: 100% !important; }\n}";document.head.appendChild(e)}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',s);else s();})();
/* End Shezair collection proportion tuning */
/* Shezair hide rail mobile facet controls */
(function(){function s(){if(document.getElementById('shz-hide-rail-mobile-controls-style'))return;var e=document.createElement('style');e.id='shz-hide-rail-mobile-controls-style';e.textContent="@media screen and (min-width: 990px) {\n  #main-collection-filters .mobile-facets__wrapper,\n  #main-collection-filters .mobile-facets,\n  #main-collection-filters .mobile-facets__heading,\n  #main-collection-filters .mobile-facets__open,\n  #main-collection-filters .mobile-facets__open-label,\n  #main-collection-filters .facets-container-drawer,\n  #main-collection-filters .facets__form:not(.facets__form-vertical),\n  #main-collection-filters .product-count__text { display: none !important; }\n}";(document.head||document.documentElement).appendChild(e)}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',s);else s();})();
/* End Shezair hide rail mobile facet controls */
/* Shezair collection polish cleanup */
(function(){function s(){if(document.getElementById('shz-collection-polish-cleanup-style'))return;var e=document.createElement('style');e.id='shz-collection-polish-cleanup-style';e.textContent="@media screen and (min-width: 990px) {\n  #main-collection-filters .facets__heading--vertical::after { content: none !important; display: none !important; }\n  #product-grid, .product-grid { list-style: none !important; padding-left: 0 !important; }\n  #product-grid .grid__item::marker, .product-grid .grid__item::marker { content: ''; font-size: 0 !important; }\n}";(document.head||document.documentElement).appendChild(e)}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',s);else s();})();
/* End Shezair collection polish cleanup */
